import React, { useEffect, useState } from 'react'
import { Button, Container, Row, Col } from 'react-bootstrap'
import { useWizard } from 'react-use-wizard'
import './styles.scss'
import { DocumentAddError, DocumentAddStep } from '../model'
import DocumentListView from 'components/organisms/DocumentListView'
import { DocumentProcessing } from 'reducers/document/documentProcessingSlice'
import { ActiveCase } from 'reducers/activecase/activecaseSlice'
import { getTotalSizeFiles } from 'share/utils/fileUtils'
import { getAppConfiguration } from 'AppSettings'

interface StepSubmitFilesProps {
  document: DocumentProcessing
  activeCase: ActiveCase
  subcategoryName: string
  subcategoryDescription: string
  files: File[]
  showAddMoreDocs?: boolean | null
  onStartedUploading: () => void
  onCancel: () => void
  onEdit: (index?: number) => void
  onDeleteFile: (index?: number) => void
}

const StepSubmitFiles = (props: StepSubmitFilesProps): JSX.Element => {
  const { previousStep, nextStep } = useWizard()
  const [error, setError] = useState(DocumentAddError.NoError)
  const docsSettings = getAppConfiguration().documents

  useEffect(() => {
    if (props.files.length === docsSettings.maxFilesUpload) {
      setError(DocumentAddError.MaxFilesError)
    }
  }, [])

  const onCancelClick = () => {
    nextStep(DocumentAddStep.Start)
    props.onCancel()
  }

  const onEditFileClick = (index?: number) => {
    previousStep(DocumentAddStep.EditFile)
    props.onEdit(index)
  }

  const onDeleteFileClick = (index?: number) => {
    if (props.files.length === 1) {
      previousStep(DocumentAddStep.Start)
    }
    setError(DocumentAddError.NoError)
    props.onDeleteFile(index)
  }

  const submitFiles = () => {
    props.onStartedUploading()
    nextStep(DocumentAddStep.Finish)
  }

  const onAddMoreDocuments = () => {
    setError(DocumentAddError.NoError)
    const totalSize = getTotalSizeFiles(props.files)

    if (totalSize === docsSettings.maxTotalFilesSize) {
      setError(DocumentAddError.TotalSizeError)
    } else if (props.files.length === docsSettings.maxFilesUpload) {
      setError(DocumentAddError.MaxFilesError)
    } else {
      previousStep(DocumentAddStep.Start)
    }
  }

  const getErrorMessage = (): string => {
    switch (error) {
      case DocumentAddError.TotalSizeError:
        return 'This file exceeds the maximum upload total size of 95MB. Please try again.'
      case DocumentAddError.MaxFilesError:
        return 'You can only upload 50 files at a time.'
      default:
        return ''
    }
  }

  return (
    <React.Fragment>
      <Container>
        <Row>
          <Col xs>
            <h2>{props.subcategoryName}</h2>
            <p>{props.subcategoryDescription}</p>
          </Col>
        </Row>
        <Row>
          <Col xs>
            <p>
              <strong>Ready to submit</strong>
            </p>
            <DocumentListView
              files={props.files}
              showPopupMenu={true}
              popupMenuActions={[
                { text: 'Edit', onClick: onEditFileClick },
                { text: 'Delete', onClick: onDeleteFileClick }
              ]}
            />
          </Col>
        </Row>
        {props.showAddMoreDocs && (
          <Row className='justify-content-md-center mt-2'>
            <Col xs md='6'>
              <Button
                hidden={error !== DocumentAddError.NoError}
                className='w-100 mt-3'
                variant='link'
                onClick={onAddMoreDocuments}
              >
                Add more documents
              </Button>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs>
            <p hidden={error !== DocumentAddError.NoError}>
              <small>
                50 files max per upload, total upload size cannot exceed 95 MB
              </small>
            </p>
            <p
              hidden={error === DocumentAddError.NoError}
              className='text-danger'
            >
              <small>{getErrorMessage()}</small>
            </p>
          </Col>
        </Row>
      </Container>
      <div className='modal-footer modal-footer-custom mt-3'>
        <Button className='footer-btn' onClick={submitFiles}>
          Submit Files
        </Button>
        <Button
          className='footer-btn btn-custom-secondary'
          variant='outline-secondary'
          onClick={onCancelClick}
        >
          Cancel
        </Button>
      </div>
    </React.Fragment>
  )
}

export default StepSubmitFiles
